import React, { useState } from 'react';
import { FiCreditCard, FiX, FiCheck } from 'react-icons/fi';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import paymentService from '../../services/paymentService';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#fff',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#a0aec0',
      },
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a',
    },
  },
};

export default function AddPaymentMethodModal({ isOpen, onClose, onSuccess }) {
  const { user } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements || !user) {
      return;
    }

    setIsLoading(true);

    try {
      // Créer un PaymentMethod avec les détails de la carte
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
        billing_details: {
          name,
        },
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // Essayer d'ajouter la méthode de paiement
      try {
        await paymentService.addPaymentMethod(user.id, {
          paymentMethodId: paymentMethod.id,
          type: 'card',
          card: {
            brand: paymentMethod.card.brand,
            last4: paymentMethod.card.last4,
            expMonth: paymentMethod.card.exp_month,
            expYear: paymentMethod.card.exp_year,
          },
        });
        toast.success('Méthode de paiement ajoutée avec succès');
        onSuccess();
        onClose();
      } catch (addPaymentError) {
        // Si l'erreur indique que le paiement n'est pas configuré
        if (addPaymentError.message.includes('configurer le paiement')) {
          // Configurer d'abord le paiement
          try {
            await paymentService.setupPayment(user.id, {
              email: user.email,
              name: user.name || user.email.split('@')[0]
            });
            
            // Réessayer d'ajouter la méthode de paiement après la configuration
            await paymentService.addPaymentMethod(user.id, {
              paymentMethodId: paymentMethod.id,
              type: 'card',
              card: {
                brand: paymentMethod.card.brand,
                last4: paymentMethod.card.last4,
                expMonth: paymentMethod.card.exp_month,
                expYear: paymentMethod.card.exp_year,
              },
            });
            
            toast.success('Méthode de paiement ajoutée avec succès');
            onSuccess();
            onClose();
          } catch (setupError) {
            console.error('Erreur lors de la configuration du paiement:', setupError);
            throw new Error('Impossible de configurer le paiement. Veuillez réessayer plus tard.');
          }
        } else {
          // Propager les autres erreurs
          throw addPaymentError;
        }
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la méthode de paiement:', error);
      toast.error(error.message || 'Une erreur est survenue lors de l\'ajout de la méthode de paiement');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="relative bg-gray-800 border border-slate-700 rounded-2xl shadow-xl w-full max-w-md">
        <div className="p-6 border-b border-slate-700 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-white">Ajouter une carte</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2 rounded-full">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Nom sur la carte
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              placeholder="Jean Dupont"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Détails de la carte
            </label>
            <div className="p-3 bg-gray-700 border border-gray-600 rounded-lg">
              <CardElement options={CARD_ELEMENT_OPTIONS} />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-6 py-2.5 bg-gray-600 text-white rounded-lg hover:bg-gray-500 disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={!stripe || isLoading}
              className="px-6 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-500 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Ajout en cours...
                </>
              ) : (
                <>
                  <FiCreditCard className="w-4 h-4" />
                  Ajouter la carte
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
